import type { IAgentConversation } from '@commenda/types';
import useSWR from 'swr';

import { listAgentConversations } from './agentConversationsApi';
import type {
  AgentConversationSummary,
  ListAgentConversationsQuery,
} from './types';

/** The list pages and the builder's history read `IAgentConversation`, keyed `id`. */
function toConversation(summary: AgentConversationSummary): IAgentConversation {
  return {
    id: summary.conversationId,
    userId: summary.userId ?? '',
    title: summary.title,
    status: summary.status as IAgentConversation['status'],
    createdByName: summary.createdByName ?? null,
    createdAt: summary.createdAt,
    updatedAt: summary.updatedAt,
  };
}

/** One agent's conversations from the unified list. A null query fetches nothing, for a caller
 * still waiting on what to filter by. */
export function useAgentConversationList(
  query: Readonly<ListAgentConversationsQuery> | null
): {
  conversations: IAgentConversation[];
  count: number;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
} {
  const { data, isLoading, error, mutate } = useSWR(
    query
      ? ([
          'agent/conversations',
          query.profile,
          query.workflowTemplateId,
          query.excludeRunSessions,
          query.companyId,
          query.scope,
          query.limit,
        ] as const)
      : null,
    () => listAgentConversations(query ?? {})
  );

  return {
    conversations: (data?.conversations ?? []).map(toConversation),
    count: data?.count ?? 0,
    isLoading,
    error,
    mutate,
  };
}
